import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { SkillConstants } from 'src/app/store/samwin/samwin-skills/skill-constants';
import { SkillGroup } from 'src/app/store/samwin/samwin-skills/skill-group';
import { CloseCallForwardDialog, OpenCallForwardDialog, SkillBrowserEntrySelected, SkillBrowserSelectNextEntry } from './skill-browser-behaviour.actions';

export interface SkillBrowserBehaviourStateModel {
   [instanceId: string]: SkillBrowserBehaviourInstance;
}

export interface SkillBrowserBehaviourInstance {
   forwardDialogOpenForSkillId: number;
   selectedSkill: number;
}

export function DefaultSkillBrowserBehaviourInstance(): SkillBrowserBehaviourInstance {
   return {
      forwardDialogOpenForSkillId: null,
      selectedSkill: null,
   };
}

@State<SkillBrowserBehaviourStateModel>({
   name: 'skillBrowserBehaviour',
   defaults: {},
})
@Injectable()
export class SkillBrowserBehaviourState {
   constructor(private store: Store) {}

   public static skillBrowserBehaviourForInstance(brickInstanceId: string) {
      return createSelector([SkillBrowserBehaviourState], (state: SkillBrowserBehaviourStateModel) => {
         return this.getSpecificBehaviourStateOrDefault(state, brickInstanceId);
      });
   }

   private static getSpecificBehaviourStateOrDefault(state: SkillBrowserBehaviourStateModel, brickInstanceId: string): SkillBrowserBehaviourInstance {
      const value = state[brickInstanceId];
      if (value) {
         return value;
      } else {
         return DefaultSkillBrowserBehaviourInstance();
      }
   }

   @Selector([SkillBrowserBehaviourState])
   public static selectedSkillId(state: SkillBrowserBehaviourStateModel) {
      return (brickInstanceId: string) => {
         return this.getSpecificBehaviourStateOrDefault(state, brickInstanceId).selectedSkill;
      };
   }

   @Selector([SkillBrowserBehaviourState.selectedSkillId, SamwinSkillsState.callSkillsInclusiveOverallSkill])
   public static selectedSkillIndex(getSelectedSkillId: (brickInstanceId: string) => number, skills: SkillGroup[]) {
      return (brickInstanceId: string) => {
         return _(skills).findIndex((value) => value.skillId === getSelectedSkillId(brickInstanceId));
      };
   }

   @Action(OpenCallForwardDialog)
   @ImmutableContext()
   public openCallForwardDialog(ctx: StateContext<SkillBrowserBehaviourStateModel>, action: OpenCallForwardDialog) {
      ctx.setState((state: SkillBrowserBehaviourStateModel) => {
         const behaviour = SkillBrowserBehaviourState.getSpecificBehaviourStateOrDefault(state, action.brickInstanceId);
         behaviour.forwardDialogOpenForSkillId = action.skillId;
         state[action.brickInstanceId] = behaviour;
         return state;
      });
   }

   @Action(CloseCallForwardDialog)
   @ImmutableContext()
   public closeCallForwardDialog(ctx: StateContext<SkillBrowserBehaviourStateModel>, action: CloseCallForwardDialog) {
      ctx.setState((state: SkillBrowserBehaviourStateModel) => {
         const behaviour = SkillBrowserBehaviourState.getSpecificBehaviourStateOrDefault(state, action.brickInstanceId);
         behaviour.forwardDialogOpenForSkillId = null;
         state[action.brickInstanceId] = behaviour;
         return state;
      });
   }

   @Action(SkillBrowserEntrySelected)
   @ImmutableContext()
   public skillBrowserSelectEntry(ctx: StateContext<SkillBrowserBehaviourStateModel>, action: SkillBrowserEntrySelected) {
      ctx.setState((state: SkillBrowserBehaviourStateModel) => {
         const behavior = SkillBrowserBehaviourState.getSpecificBehaviourStateOrDefault(state, action.brickInstanceId);
         behavior.selectedSkill = action.skillId;
         state[action.brickInstanceId] = behavior;
         return state;
      });
   }

   @Action(SkillBrowserSelectNextEntry)
   @ImmutableContext()
   public selectNextEntry(ctx: StateContext<SkillBrowserBehaviourStateModel>, action: SkillBrowserSelectNextEntry) {
      const currentValues: SkillGroup[] = this.store.selectSnapshot(SamwinSkillsState.callSkillsInclusiveOverallSkill);

      const skillState = SkillBrowserBehaviourState.getSpecificBehaviourStateOrDefault(ctx.getState(), action.brickInstanceId);
      let currentIndex = _(currentValues).findIndex((value) => value.skillId === skillState.selectedSkill);
      if (_.isNil(currentIndex)) {
         currentIndex = -1;
      }
      const lastIndex = _(currentValues).findLastIndex();
      let nextIndex = currentIndex + (action.direction === 'up' ? -action.step : action.step);
      if (nextIndex > lastIndex) {
         nextIndex = lastIndex;
      }
      if (nextIndex < 0) {
         nextIndex = 0;
      }
      let selectedSkill = currentValues[nextIndex]?.skillId;
      if (_.isNil(selectedSkill)) {
         return;
      }
      if (selectedSkill === SkillConstants.OVERALL_SKILL_ID) {
         selectedSkill = null;
      }
      return ctx.dispatch(new SkillBrowserEntrySelected(action.brickInstanceId, selectedSkill));
   }
}
